import { useState } from 'react';
import { Plus, Trash2, Edit2, Clock, CheckCircle2, XCircle, Ban } from 'lucide-react';
import type { PrintWithRelations, PrintQuery } from '@maker-toolchest/shared';
import { Button } from '@/components/Button';
import { Spinner } from '@/components/Spinner';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { usePrints, useDeletePrint } from '../hooks';
import { PrintForm } from './PrintForm';

interface PrintHistoryTableProps {
  printerId?: string; // Optional - if provided, only shows prints for this printer
  onViewPrint?: (print: PrintWithRelations) => void;
}

export function PrintHistoryTable({ printerId }: PrintHistoryTableProps) {
  const filters: PrintQuery = {
    page: 1,
    limit: 20,
    sortBy: 'startedAt',
    sortOrder: 'desc',
    tenantPrinterId: printerId,
  };
  const [isAddingPrint, setIsAddingPrint] = useState(false);
  const [editingPrint, setEditingPrint] = useState<PrintWithRelations | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<PrintWithRelations | null>(null);

  const { data, isLoading } = usePrints(filters);
  const deleteMutation = useDeletePrint();

  const prints = data || [];

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await deleteMutation.mutateAsync(deleteTarget.id);
      setDeleteTarget(null);
    } catch {
      // Error handled by mutation
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'COMPLETED':
        return <CheckCircle2 className="h-4 w-4 text-success" />;
      case 'FAILED':
        return <XCircle className="h-4 w-4 text-error" />;
      case 'CANCELLED':
        return <Ban className="h-4 w-4 text-warning" />;
      default:
        return <Clock className="h-4 w-4 text-primary" />;
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'COMPLETED':
        return 'Completed';
      case 'FAILED':
        return 'Failed';
      case 'CANCELLED':
        return 'Cancelled';
      default:
        return 'In Progress';
    }
  };

  const formatDuration = (hours: number | null | undefined) => {
    if (!hours) return 'N/A';
    if (hours < 1) return `${Math.round(hours * 60)}m`;
    return `${hours.toFixed(1)}h`;
  };

  if (isLoading && !data) {
    return (
      <div className="flex items-center justify-center py-12">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-medium text-foreground">Print History</h3>
        <Button size="sm" leftIcon={<Plus className="h-4 w-4" />} onClick={() => setIsAddingPrint(true)}>
          Log Print
        </Button>
      </div>

      {prints.length === 0 ? (
        <div className="rounded-lg border border-border bg-background-subtle p-8 text-center">
          <p className="text-foreground-muted">No print history yet. Log your first print job to start tracking.</p>
        </div>
      ) : (
        <>
          <div className="overflow-hidden rounded-lg border border-border">
            <table className="min-w-full divide-y divide-border">
              <thead className="bg-background-subtle">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-foreground-muted">
                    Print Job
                  </th>
                  {!printerId && (
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-foreground-muted">
                      Printer
                    </th>
                  )}
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-foreground-muted">
                    Status
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-foreground-muted">
                    Started
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-foreground-muted">
                    Duration
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wider text-foreground-muted">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border bg-background">
                {prints.map((print: PrintWithRelations) => (
                  <tr key={print.id} className="hover:bg-background-subtle/50">
                    <td className="whitespace-nowrap px-4 py-3">
                      <div>
                        <div className="text-sm font-medium text-foreground">
                          {print.name}
                        </div>
                        {print.fileName && (
                          <div className="text-xs text-foreground-muted">
                            {print.fileName}
                          </div>
                        )}
                      </div>
                    </td>
                    {!printerId && (
                      <td className="whitespace-nowrap px-4 py-3 text-sm text-foreground-muted">
                        {print.tenantPrinter.instanceName || print.tenantPrinter.printer.name}
                      </td>
                    )}
                    <td className="whitespace-nowrap px-4 py-3">
                      <div className="flex items-center gap-2">
                        {getStatusIcon(print.status)}
                        <span className="text-sm text-foreground-muted">
                          {getStatusLabel(print.status)}
                        </span>
                      </div>
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-sm text-foreground-muted">
                      {new Date(print.startedAt).toLocaleString()}
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-sm text-foreground-muted">
                      {formatDuration(print.printDuration)}
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setEditingPrint(print)}
                          title="Edit print"
                        >
                          <Edit2 className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setDeleteTarget(print)}
                          title="Delete print"
                        >
                          <Trash2 className="h-4 w-4 text-error" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* TODO: Pagination support */}
          {/* {pagination && pagination.totalPages > 1 && (
            <Pagination
              page={pagination.page}
              totalPages={pagination.totalPages}
              total={pagination.total}
              limit={pagination.limit}
              onPageChange={handlePageChange}
            />
          )} */}
        </>
      )}

      {/* Add/Edit Form */}
      <PrintForm
        open={isAddingPrint || !!editingPrint}
        onClose={() => {
          setIsAddingPrint(false);
          setEditingPrint(null);
        }}
        print={editingPrint}
        printerId={printerId}
      />

      {/* Delete Confirmation */}
      <ConfirmDialog
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDelete}
        title="Delete Print Job"
        description={`Are you sure you want to delete "${deleteTarget?.name}"? This will affect printer statistics.`}
        confirmText="Delete"
        variant="danger"
        loading={deleteMutation.isPending}
      />
    </div>
  );
}
